import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import API_BASE_URL from "./config";
import "./LiveSlideshow.css";
import publicOrigin from "./publicOrigin";

const POLL_INTERVAL_MS = 5000;
const ADVANCE_INTERVAL_MS = 6000;

function LiveSlideshow() {
  const { token } = useParams();
  const containerRef = useRef(null);
  const lastIdRef = useRef(0);

  const [studioName, setStudioName] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState("");

  // Studio/event info, once
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/shared/${token}`);
        if (!res.ok) throw new Error("Invalid link");
        const data = await res.json();
        setStudioName(data.studioName || data.event?.event_name || "");
        setLogoUrl(data.logoUrl || null);
      } catch {
        setError("This slideshow link is invalid or has expired.");
      }
    })();
  }, [token]);

  // Poll for new photos — sinceId keeps every call after the first cheap.
  const poll = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/shared/${token}/slideshow-images?sinceId=${lastIdRef.current}`
      );
      if (!res.ok) return;
      const data = await res.json();
      if (data.images?.length) {
        setImages((prev) => {
          const seen = new Set(prev.map((img) => img.id));
          const fresh = data.images.filter((img) => !seen.has(img.id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
      if (typeof data.lastId === "number") {
        lastIdRef.current = Math.max(lastIdRef.current, data.lastId);
      }
    } catch {
      // Transient network hiccup — next poll cycle will catch up.
    }
  }, [token]);

  useEffect(() => {
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [poll]);

  // Auto-advance — independent of polling, just walks the (possibly growing) array.
  useEffect(() => {
    if (images.length === 0) return;
    const id = setInterval(() => {
      setCurrentIndex((i) => (i + 1) % images.length);
    }, ADVANCE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [images.length]);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen().catch(() => {});
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const galleryUrl = `${publicOrigin()}/shared/${token}`;

  if (error) {
    return <div className="ls-status">{error}</div>;
  }

  return (
    <div className="ls-stage" ref={containerRef}>
      {images.length === 0 ? (
        <div className="ls-status">
          <div className="ls-spinner" />
          <p>Waiting for photos...</p>
        </div>
      ) : (
        images.map((img, i) => (
          <div
            key={img.id}
            className={`ls-slide ${i === currentIndex ? "ls-slide--active" : ""}`}
          >
            <img src={img.image_url} alt="" />
          </div>
        ))
      )}

      {logoUrl && (
        <div className="ls-corner ls-corner--logo">
          <img src={logoUrl} alt={studioName} />
        </div>
      )}

      <div className="ls-corner ls-corner--qr">
        <QRCodeSVG value={galleryUrl} size={84} level="M" includeMargin bgColor="#ffffff" />
        <span>Scan for your photos</span>
      </div>

      {studioName && <div className="ls-studio-name">{studioName}</div>}

      <button className="ls-fullscreen-btn" onClick={toggleFullscreen}>
        {isFullscreen ? "⤢ Exit Fullscreen" : "⛶ Fullscreen"}
      </button>

      {/* Desktop only: throw the show onto the projector/second display while
          this window stays usable for the studio. */}
      {window.photentra?.presentOnDisplay && !isFullscreen && (
        <button
          className="ls-fullscreen-btn"
          style={{ left: "auto", right: 24 }}
          onClick={() => window.photentra.presentOnDisplay(window.location.pathname)}
        >
          🖥 Present on second display
        </button>
      )}
    </div>
  );
}

export default LiveSlideshow;
